import type { SwimlaneLayoutDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import { applyHeaderOrder } from '@/components/views/swimlane/utils/applyHeaderOrder';

type VisualizationLayout = SwimlaneLayoutDTO | TimelineLayoutDTO | null | undefined;

/**
 * Orders header values for display in the color legend.
 *
 * This logic determines the order in which field values appear in the legend, which controls
 * which color each value gets from the palette. The legend order can differ from the swimlane
 * header display order - they serve different purposes:
 * - Swimlane header order (columnHeaderOrder/rowHeaderOrder): Visual left-to-right or top-to-bottom arrangement
 * - Legend order (derived here): Determines color assignments (value at index 0 gets color 0, etc.)
 *
 * Priority System (applied in order):
 *
 * 1. HIGHEST: headerColorOrder (color legend reordering)
 *    - When user drags items in THIS color legend to reorder them
 *    - Stored in: layout.colorConfig.headerColorOrder
 *    - Purpose: Controls which color each value receives
 *    - When valid: ALWAYS takes precedence over all other orders
 *
 * 2. MEDIUM: displayOrder (swimlane header drag-and-drop)
 *    - When user drags headers in the swimlane view to reorder them
 *    - Stored in: layout.columnHeaderOrder or layout.rowHeaderOrder (depending on which axis)
 *    - Purpose: Provides initial color order based on visual arrangement
 *    - Only used: When no headerColorOrder exists yet, or as a filter for valid values
 *
 * 3. FALLBACK: Backend order (uniqueHeaderValues)
 *    - Original order from the backend/database
 *    - Used when: No display order AND no color order exist
 *    - Provides: Stable, consistent starting point
 *
 * Logic Flow:
 * ```
 * Step 1: Determine which display order applies
 *   - If coloring by header1 field → use columnHeaderOrder
 *   - If coloring by header2 field → use rowHeaderOrder
 *   - Otherwise → no display order
 *
 * Step 3a: If display order exists:
 *   - Apply display order using applyHeaderOrder() → get orderedValues
 *   - Check if headerColorOrder is valid (contains values from orderedValues)
 *   - If valid: Merge headerColorOrder with orderedValues → RETURN merged order
 *   - If not: RETURN orderedValues (display order)
 *
 * Step 3b: If no display order:
 *   - Check if headerColorOrder is valid (contains values from uniqueHeaderValues)
 *   - If valid: Merge headerColorOrder with uniqueHeaderValues → RETURN merged order
 *   - If not: RETURN uniqueHeaderValues (backend order)
 * ```
 *
 * Merge Logic (when applying headerColorOrder):
 * - Start with the saved headerColorOrder array
 * - Append any NEW values that don't exist in headerColorOrder (e.g., new status added)
 * - Filter out REMOVED values that no longer exist in current values (e.g., status deleted)
 * - Result: Stable color assignments that gracefully handle data changes
 *
 * Example Scenarios:
 *
 * Scenario A: User reorders colors in legend
 * - Initial: ["To Do", "In Progress", "Done"]
 * - User drags in legend: ["Done", "To Do", "In Progress"]
 * - headerColorOrder saved: ["Done", "To Do", "In Progress"]
 * - Result: Legend shows ["Done", "To Do", "In Progress"], colors match this order
 *
 * Scenario B: User reorders headers in swimlane (no prior color order)
 * - Initial: ["To Do", "In Progress", "Done"]
 * - User drags headers: ["Done", "To Do", "In Progress"]
 * - columnHeaderOrder saved: ["Done", "To Do", "In Progress"]
 * - No headerColorOrder exists
 * - Result: Legend uses display order ["Done", "To Do", "In Progress"]
 *
 * Scenario C: Both display and color orders exist
 * - Display order: ["Done", "To Do", "In Progress"]
 * - Color order: ["In Progress", "Done", "To Do"]
 * - Result: Color order WINS → ["In Progress", "Done", "To Do"]
 * - Swimlane headers still display in their own order, but colors follow legend order
 */
export function useOrderedHeaderValues(
  uniqueHeaderValues: string[],
  currentHeaderColorOrder: string[],
  currentColorByField: string,
  _layout: VisualizationLayout,
  header1FieldId: string | null,
  header2FieldId: string | null,
  columnHeaderOrder?: string[],
  rowHeaderOrder?: string[],
): string[] {
  return useMemo(() => {
    if (uniqueHeaderValues.length === 0) return [];

    // Apply the same ordering logic that swimlane uses
    // First, determine which header order to use (column or row)
    let displayOrder: string[] | null = null;
    if (currentColorByField === header1FieldId) {
      displayOrder = columnHeaderOrder || null;
    } else if (currentColorByField === header2FieldId) {
      displayOrder = rowHeaderOrder || null;
    }

    // If we have a display order from layout (drag-and-drop reordering), apply it
    // This matches what applyHeaderOrder does in the swimlane
    if (displayOrder && displayOrder.length > 0) {
      // Create minimal header structure for applyHeaderOrder
      // We only need the id field for ordering, other fields are not used
      const headerLikeItems = uniqueHeaderValues.map((v) => ({
        id: v,
        label: v,
        headerColor: '',
        isDateHeader: false,
        selector: { type: 'CustomPropertySelectorByID', customPropertyID: '' } as const,
      }));
      const orderedHeaders = applyHeaderOrder(headerLikeItems, displayOrder);
      const orderedValues = orderedHeaders.map((h) => h.id);

      // Now apply the color order (headerColorOrder) if it matches the field
      const savedOrderMatchesCurrentField =
        currentHeaderColorOrder.length > 0 &&
        currentHeaderColorOrder.some((value) => orderedValues.includes(value));

      if (savedOrderMatchesCurrentField) {
        const colorOrdered = [...currentHeaderColorOrder];
        const existingSet = new Set(currentHeaderColorOrder);

        // Append new values not in saved order
        orderedValues.forEach((value) => {
          if (!existingSet.has(value)) {
            colorOrdered.push(value);
          }
        });

        // Filter out values that no longer exist
        return colorOrdered.filter((value) => orderedValues.includes(value));
      }

      return orderedValues;
    }

    // No display order - use color order if it matches the field
    const savedOrderMatchesCurrentField =
      currentHeaderColorOrder.length > 0 &&
      currentHeaderColorOrder.some((value) => uniqueHeaderValues.includes(value));

    if (savedOrderMatchesCurrentField) {
      const ordered = [...currentHeaderColorOrder];
      const existingSet = new Set(currentHeaderColorOrder);

      // Append new values not in saved order
      uniqueHeaderValues.forEach((value) => {
        if (!existingSet.has(value)) {
          ordered.push(value);
        }
      });

      // Filter out values that no longer exist
      return ordered.filter((value) => uniqueHeaderValues.includes(value));
    }

    // No saved order or saved order is for a different field
    // Preserve the order from uniqueHeaderValues (backend order)
    return uniqueHeaderValues;
  }, [
    uniqueHeaderValues,
    currentHeaderColorOrder,
    currentColorByField,
    header1FieldId,
    header2FieldId,
    columnHeaderOrder,
    rowHeaderOrder,
  ]);
}
